import { useEffect, useRef, useState } from "react";
import { Question, X, PaperPlaneRight, CaretDown, Headset } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError, logError } from "../lib/api";

const newSession = () => `aide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function FaqList() {
  const [faq, setFaq] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/help/faq").then((r) => setFaq(r.data)).catch((e) => logError(e)); }, []);
  return (
    <div className="space-y-1.5" data-testid="help-faq">
      {faq.map((f, i) => (
        <div key={f.q} className="rounded-xl bg-[#F6F4EF]/70 overflow-hidden">
          <button data-testid="help-faq-question" onClick={() => setOpen(open === i ? null : i)}
            className="w-full text-left px-3 py-2.5 text-sm font-semibold flex items-start gap-2">
            <span className="flex-1">{f.q}</span>
            <CaretDown size={14} className={`shrink-0 mt-1 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <p data-testid="help-faq-answer" className="px-3 pb-3 text-xs text-gray-600">{f.a}</p>}
        </div>
      ))}
    </div>
  );
}

function HelpChat() {
  const [sessionId] = useState(newSession);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalated, setEscalated] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const ask = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setQuestion(""); setBusy(true);
    try {
      const { data } = await api.post("/help/ask", { session_id: sessionId, question: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/help/escalate", { session_id: sessionId });
      setEscalated(data.ticket.number);
      toast.success(`Ticket #${data.ticket.number} créé — l'équipe Kavizio a reçu votre conversation`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="help-chat">
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {messages.length === 0 && (
          <p className="text-xs text-gray-500 bg-[#F6F4EF]/70 rounded-xl px-3 py-2.5">
            Posez votre question sur l'utilisation de Kavizio : « comment enregistrer une vente ? », « comment inviter un vendeur ? »…
          </p>
        )}
        {messages.map((m, i) => (
          <div key={`${m.role}-${i}`} data-testid={`help-msg-${m.role}`}
            className={`text-sm rounded-2xl px-3 py-2 max-w-[92%] ${m.role === "user" ? "ml-auto bg-[#16233D] text-white" : "bg-[#F6F4EF]"}`}>
            {m.content.replaceAll("**", "")}
          </div>
        ))}
        {busy && <p className="text-xs text-gray-400">L'assistant rédige...</p>}
        <div ref={endRef} />
      </div>

      <form onSubmit={ask} className="flex gap-2 pt-2">
        <input data-testid="help-chat-input" className="kinput flex-1" placeholder="Votre question"
          value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button data-testid="help-chat-send" className="btn-amber px-3" disabled={busy || !question.trim()}><PaperPlaneRight size={16} /></button>
      </form>

      {escalated ? (
        <p data-testid="help-escalated" className="text-xs text-[#2E7D5B] pt-2">
          Ticket #{escalated} ouvert. Un agent Kavizio vous répondra ; la conversation lui a été transmise.
        </p>
      ) : (
        <button data-testid="help-escalate-btn" onClick={escalate} disabled={busy}
          className="text-xs text-gray-500 hover:text-[#16233D] pt-2 flex items-center gap-1.5 self-start">
          <Headset size={13} /> Parler à un humain
        </button>
      )}
    </div>
  );
}

export function HelpWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("faq");

  return (
    <>
      <button data-testid="help-fab" onClick={() => setOpen(true)} aria-label="Aide"
        className="help-fab fixed z-40 bottom-24 right-4 md:bottom-6 md:right-6 w-12 h-12 rounded-full bg-[#16233D] text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform">
        <Question size={22} weight="bold" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setOpen(false)}>
          <div data-testid="help-panel" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 h-[85dvh] sm:h-[600px] flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-display font-bold text-lg flex-1">Aide</h2>
              <button data-testid="help-close" onClick={() => setOpen(false)} className="p-1 text-gray-500"><X size={20} /></button>
            </div>
            <div className="flex gap-1.5 mb-3">
              {[["faq", "Questions fréquentes"], ["chat", "Assistant"]].map(([k, l]) => (
                <button key={k} data-testid={`help-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === k ? "bg-[#16233D] text-white" : "bg-[#F6F4EF] text-gray-600"}`}>{l}</button>
              ))}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {tab === "faq" ? <FaqList /> : <HelpChat />}
            </div>
            <p className="text-xs text-gray-400 pt-2">
              Cette aide porte sur l'utilisation du logiciel. Pour analyser vos chiffres, utilisez l'assistant du dashboard.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
